import { ListingResource } from '../system/field';
import { TaskManager } from './TaskManager';

/**
 * Base interface for all RESO resources
 */
export interface ResoResourceBase {
  // Base fields for all resources
  getKey(): string | number | undefined;
  getItemNumber(): number | undefined;
  getListingKeyField(): string | undefined;
  getListingKey(): string | undefined;
}

export interface ResourceManagerOptions {
  enableCwa?: boolean;
  useTranslation?: boolean;
  autoPage?: number;
}

/**
 * Interface for all resource managers
 */
export interface ResoResourceManager<T extends ResoResourceBase> {
  // CRUD operations
  list(listingKey?: string): Promise<T[]>;
  getById(id: string | number): Promise<T | null>;
  create(resource: T): Promise<T>;
  update(resource: T): Promise<T>;
  delete(resource: T | string | number): Promise<boolean>;
  save(resource: T): Promise<T>;

  // Find operations
  find(predicate: (item: T) => boolean): Promise<T | null>;
  findByField(field: string, value: any): Promise<T | null>;
  findByFields(fields: Record<string, any>): Promise<T | null>;
  findListByField(field: string, value: any): Promise<T[]>;
  findListByFields(fields: Record<string, any>): Promise<T[]>;

  // Task tracking
  isTaskInProgress(taskId?: string): boolean;
  isUpdatingItem(resourceId: string | number): boolean;
  isAnyTaskInProgress(): boolean;

  // Mode operations - will only be implemented for listing resources
  cwa(): ResoResourceManager<T>;
  // Add access to task manager
  taskManager: TaskManager;
}

/**
 * Configuration for a resource type
 */
export interface BaseResourceConfig {
  entityName: string; // Resource entity name in RESO API
  keyField: string; // Primary key field (without CWA prefix)
  supportsCwa: boolean; // Whether this resource type supports CWA mode
  hasListingContext: boolean; // Whether this resource is tracked in listing context
}

/**
 * Configuration for listing-related resources
 */
export interface ListingResourceConfig extends BaseResourceConfig {
  supportsCwa: true;
  hasListingContext: true;
  liveKeyField: string; // Key field for non-CWA mode
  listingKeyField: string; // Field that references the listing key
  itemNumberField?: string; // Sequence number field for the resource, if available
  modificationTimestampField?: string; // Field tracking last modification time
  lisKeyField?: string; // Field that references the LIS key (CWA mode)
  itemKey?: string;
}

/**
 * Configuration for external (non-listing) resources
 */
export interface ExternalResourceConfig extends BaseResourceConfig {
  supportsCwa: false;
  hasListingContext: false;
  modificationTimestampField?: string; // Field tracking last modification time
}

// Type guard to check if config is for a listing resource
export function isListingResourceConfig(
  config: BaseResourceConfig,
): config is ListingResourceConfig {
  return config.supportsCwa === true && config.hasListingContext === true;
}

// Configuration for all standard listing resource types
export const ListingResourceConfigs: Record<
  ListingResource,
  ListingResourceConfig
> = {
  [ListingResource.Media]: {
    entityName: 'Media',
    keyField: 'MEDVWKey',
    liveKeyField: 'MediaKey',
    listingKeyField: 'ResourceRecordKey',
    itemNumberField: 'MediaItemNumber',
    modificationTimestampField: 'MediaModificationTimestamp',
    lisKeyField: 'MEDVWLISKey',
    supportsCwa: true,
    hasListingContext: true,
  },
  [ListingResource.OpenHouse]: {
    entityName: 'OpenHouse',
    keyField: 'UOHVWKey',
    liveKeyField: 'OpenHouseKey',
    listingKeyField: 'OpenHouseListingKey',
    itemNumberField: 'OpenHouseItemNumber',
    modificationTimestampField: 'UOHVWChangeTimestamp',
    lisKeyField: 'UOHVWLISKey',
    supportsCwa: true,
    hasListingContext: true,
    itemKey: 'UOHVW_KEY',
  },
  [ListingResource.Unit]: {
    entityName: 'Unit',
    keyField: 'UnitTypeKey',
    liveKeyField: 'UnitTypeKey',
    listingKeyField: 'UnitTypeListingKey',
    itemNumberField: 'UnitTypeItemNumber',
    modificationTimestampField: 'UUNITChangeTimestamp',
    lisKeyField: 'UUNITLISKey',
    supportsCwa: true,
    hasListingContext: true,
  },
  [ListingResource.Room]: {
    entityName: 'Room',
    keyField: 'ROOMKey',
    liveKeyField: 'RoomKey',
    listingKeyField: 'RoomListingKey',
    itemNumberField: 'RoomItemNumber',
    modificationTimestampField: 'ROOMChangeTimestamp',
    lisKeyField: 'ROOMLISKey',
    supportsCwa: true,
    hasListingContext: true,
  },
  [ListingResource.GreenVerification]: {
    entityName: 'GreenVerification',
    keyField: 'GVKey',
    liveKeyField: 'GreenVerificationKey',
    listingKeyField: 'GreenVerificationListingKey',
    modificationTimestampField: 'GVChangeTimestamp',
    lisKeyField: 'GVLISKey',
    supportsCwa: true,
    hasListingContext: true,
  },
  [ListingResource.Property]: {
    entityName: 'Property',
    keyField: 'ListingKey',
    liveKeyField: 'ListingKey',
    listingKeyField: 'ListingKey',
    modificationTimestampField: 'ModificationTimestamp',
    supportsCwa: true,
    hasListingContext: true,
  },
};

// Configuration for external (non-listing) resource types
export const ExternalResourceConfigs: Record<string, ExternalResourceConfig> = {
  Agent: {
    entityName: 'Agent',
    keyField: 'AgentKey',
    modificationTimestampField: 'AgentModificationTimestamp',
    supportsCwa: false,
    hasListingContext: false,
  },
  School: {
    entityName: 'School',
    keyField: 'SchoolKey',
    supportsCwa: false,
    hasListingContext: false,
  },
  Office: {
    entityName: 'Office',
    keyField: 'OfficeKey',
    modificationTimestampField: 'OfficeModificationTimestamp',
    supportsCwa: false,
    hasListingContext: false,
  },
};

// Get configuration for any resource type
export function getResourceConfig(
  resourceType: ListingResource | string,
): BaseResourceConfig {
  // Check if it's a listing resource
  if (
    Object.values(ListingResource).includes(resourceType as ListingResource)
  ) {
    return ListingResourceConfigs[resourceType as ListingResource];
  }

  // Check if it's a known external resource
  if (
    typeof resourceType === 'string' &&
    ExternalResourceConfigs[resourceType]
  ) {
    return ExternalResourceConfigs[resourceType];
  }

  // For unknown resources, return a minimal configuration
  return {
    entityName: String(resourceType),
    keyField: 'Key',
    supportsCwa: false,
    hasListingContext: false,
  };
}

// Task tracking interfaces
export interface TaskStatus {
  inProgress: boolean;
  taskId: string;
  resourceType: ListingResource | string;
  operation: 'create' | 'update' | 'delete' | 'list' | 'other';
  startTime: number;
  endTime?: number;
  resource?: any;
  resourceId?: string | number;
  taskName?: string;
}

export interface ResourceResult<T> {
  success: boolean;
  data?: T;
  error?: {
    message: string;
    code?: string;
  };
  taskId?: string;
}
